import {
  BooleanInput, coerceBooleanProperty,
} from '@angular/cdk/coercion'
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostBinding, Input, inject,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  ControlValueAccessor, FormGroupDirective, NgControl, NgForm, Validators,
} from '@angular/forms'

/**
 * Control IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueIdCounter = 0
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,

})
export abstract class BaseControlComponent<T> implements ControlValueAccessor {
  protected uniqueId = `${_uniqueIdCounter++}`

  @Input()
  public get name(): string { return this._name }

  public set name(newValue: string) {
    this._name = newValue
    this.nameUpdated?.()
  }

  private _name!: string
  protected nameUpdated?(): void

  @Input() public tabindex?: string

  @HostBinding('class.kb-submitted')
  private submitted = false

  @HostBinding('attr.disabled')
  private disabledAttribute?: 'disabled'

  @Input()
  public get disabled(): boolean { return this._disabled }

  public set disabled(value: BooleanInput) {
    this.setDisabledState(coerceBooleanProperty(value))
  }

  private _disabled = false

  @Input()
  public get required(): boolean { return this._required ? this._required : ((this.topNgControl?.control?.hasValidator(Validators.required) || this.topNgControl?.control?.hasValidator(Validators.requiredTrue)) ?? false) }

  public set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value)
  }

  private _required = false

  protected get showError(): boolean {
    return !!(this.topNgControl?.invalid && (this.submitted || this.topNgControl?.touched))
  }

  public topNgControl = inject(NgControl, { optional: true })
  public ngControl = inject(NgControl, {
    optional: true,
    self: true,
  })

  private parentForm = inject(NgForm, { optional: true })
  private parentFormGroup = inject(FormGroupDirective, { optional: true })
  protected ref = inject(ChangeDetectorRef)
  protected destroyRef = inject(DestroyRef)

  public constructor() {
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this
    }

    if (this.parentFormGroup) { // Reactive form
      this.setupSubmittedChangeDetector(this.parentFormGroup)
    } else if (this.parentForm) { // Template driven form
      this.setupSubmittedChangeDetector(this.parentForm)
    }
  }

  private setupSubmittedChangeDetector(form: NgForm | FormGroupDirective): void {
    form.ngSubmit.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.submitted = true
      this.ref.markForCheck() // update showError
    })

    const originalResetForm = form.resetForm
    form.resetForm = (value?: T): void => {
      originalResetForm.apply(form, [value])
      this.submitted = false
      this.ref.markForCheck() // update showError
    }
  }

  public setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled
    this.disabledAttribute = this.disabled ? 'disabled' : undefined
  }

  public abstract writeValue(value: T): void
  public abstract registerOnChange(onChange: (value: T) => void): void
  public abstract registerOnTouched(onTouched: () => void): void
}
